import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { AppInformationService, AuthService, BrewersUnitOfWork } from 'app/core/core.module';
import { Statement } from 'app/core/model/entity-model';
import { ReportService } from 'app/core/report.service';
import { InputDialogComponent } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import { BsModalService } from 'ngx-bootstrap/modal';

@Component({
    standalone: false,
    selector: 'app-statement-overview',
    template: `
        <div class="flex-fill d-flex flex-column p-3">
            <div class="grid-fill">
                <dx-data-grid #dataGrid [dataSource]="statements" keyExpr="id" (onCellDblClick)="open(dataGrid.instance.getSelectedRowsData()[0])">
                    <dxi-column dataField="name" caption="Name"></dxi-column>
                    <dxi-column dataField="customerNames" caption="Customers"></dxi-column>
                    <dxi-column dataField="created" caption="Created" dataType="datetime" width="150"></dxi-column>
                    <dxi-column dataField="createdBy" caption="Created By" width="150"></dxi-column>
                    <dxi-column></dxi-column>

                    <dxo-search-panel class="d-none d-sm-block" [visible]="true"></dxo-search-panel>
                    <dxo-export [enabled]="true" [allowExportSelectedData]="true"></dxo-export>
                    <dxo-filter-row [visible]="true"></dxo-filter-row>
                    <dxo-state-storing [enabled]="true" type="localStorage" storageKey="statement-search-grid"></dxo-state-storing>

                    <div *dxTemplate="let data of 'toolbarButtons'">
                        <button
                            class="btn btn-sm btn-primary me-2"
                            (click)="open(dataGrid.instance.getSelectedRowsData()[0])"
                            [disabled]="dataGrid.instance.getSelectedRowsData().length !== 1"
                            >
                            Open
                        </button>
                        <button
                            class="btn btn-sm btn-primary me-2"
                            (click)="rename(dataGrid.instance.getSelectedRowsData()[0])"
                            [disabled]="dataGrid.instance.getSelectedRowsData().length !== 1"
                            >
                            Rename
                        </button>
                        <button
                            class="btn btn-sm btn-danger me-4"
                            (click)="delete(dataGrid.instance.getSelectedRowsData())"
                            [disabled]="dataGrid.instance.getSelectedRowsData().length === 0"
                            >
                            Delete
                        </button>
                    </div>
                    <div *dxTemplate="let data of 'toolbarText'">
                        @if (statements) {
                        <span class="me-3">
                            <strong>{{ dataGrid.instance.totalCount() + " items" }}</strong>
                        </span>
                        }
                    </div>
                </dx-data-grid>
            </div>
        </div>
    `,
    styles: [``],
})
export class StatementOverviewComponent implements OnInit {
    uow = inject(BrewersUnitOfWork);
    auth = inject(AuthService);
    appInfo = inject(AppInformationService);
    modalService = inject(BsModalService);
    reportService = inject(ReportService);

    statements: Statement[];

    customerNamesById = new Map<number, string>();

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    async ngOnInit() {
        this.appInfo.pleaseWaitShow();
        await this.loadCustomerNames();
        await this.search();
        this.appInfo.pleaseWaitHide();
    }

    async loadCustomerNames() {
        const customers = (await this.uow.customers.getActiveCustomers(null, 'id, customerUnixKey, companyName')) as any[];
        customers.forEach((customer) => this.customerNamesById.set(customer.id, customer.companyName || customer.customerUnixKey));
    }

    async search() {
        const statements = await this.uow.getStatements();
        statements.forEach((statement) => (statement.customerNames = this.getCustomerNames(statement)));
        this.statements = statements;
    }

    getCustomerNames(statement: Statement) {
        if (!statement.customerIds) return '';
        return statement.customerIds
            .split(',')
            .map((id) => this.customerNamesById.get(Number(id)))
            .filter((name) => !!name)
            .join(', ');
    }

    open(statement: Statement) {
        if (!statement) return;
        this.reportService.viewStatement(statement);
    }

    async rename(statement: Statement) {
        if (!statement) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Rename Statement',
                mode: 'text',
                dialogValue: statement.name,
            },
        });

        modalRef.content?.result.subscribe(async (result) => {
            if (result && result.success) {
                this.appInfo.pleaseWaitShow();
                statement.name = result.value;
                await this.uow.commit([statement]);
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async delete(statements: Statement[]) {
        if (!statements || statements.length === 0) return;

        const result = await confirm('Are you sure you want to delete the selected statement(s)?', this.appInfo.settings.company);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        statements.forEach((statement) => {
            statement.deleted = new Date();
            statement.deletedBy = this.auth.currentUser.userName;
        });
        await this.uow.commit(statements);
        await this.search();
        this.appInfo.pleaseWaitHide();
    }
}
